import { useCallback, useMemo, useRef } from 'react'

import { toast } from 'react-toastify'

import {
  BookingChargeDefaultsByType,
  isActiveOutOfHoursCharge,
  representsOutOfHours,
} from 'constants/EntityConstants/OtherChargeEntity'
import { BookingChargeType, useGetOutOfHoursChargeRateQuery } from 'generated/graphql'
import { TBookingAddressForm, TBookingChargeForm } from 'types/form-types'

import { getChargeKey, isOutOfHoursTimestamp } from './utils'

type IUseOutOfHoursChargeSyncProps = {
  collectionAddresses: TBookingAddressForm[]
  customerId?: string
  consignmentFee?: number
  isPricingLocked?: boolean
}

export const useOutOfHoursChargeSync = ({
  collectionAddresses,
  customerId,
  consignmentFee,
  isPricingLocked = false,
}: IUseOutOfHoursChargeSyncProps) => {
  const { data: outOfHoursRateData } = useGetOutOfHoursChargeRateQuery({
    variables: {
      customerId: customerId ? Number(customerId) : null,
      consignmentFee: Number(consignmentFee) || 0,
    },
    fetchPolicy: 'cache-first',
  })

  const serverRate = Number(outOfHoursRateData?.outOfHoursChargeRate)
  const outOfHoursRate = serverRate > 0 ? serverRate : null

  const automaticallyDisabledChargeKeys = useRef(new Set<string>())

  const handleOutOfHoursChargesApplied = useCallback(
    (charges: TBookingChargeForm[]) => {
      if (outOfHoursRate === null) {
        return
      }

      const existingAutomaticChargeIndex = charges.findIndex(
        (charge) => isActiveOutOfHoursCharge(charge) && charge?.isAutomatic,
      )

      if (existingAutomaticChargeIndex !== -1) {
        const existingCharge = charges[existingAutomaticChargeIndex]

        if (Number(existingCharge.rate) !== outOfHoursRate) {
          charges[existingAutomaticChargeIndex] = {
            ...existingCharge,
            rate: outOfHoursRate,
            quantity: 1,
          }
          toast.info(`Automatic charge Out Of Hours was updated.`)
        }

        return
      }

      const hasActiveOutOfHoursCharge = charges.some(isActiveOutOfHoursCharge)
      const switchedOffAutomaticChargeIndex = hasActiveOutOfHoursCharge
        ? -1
        : charges.findIndex(
            (charge) => representsOutOfHours(charge) && charge?.isAutomatic && charge.isDeleted,
          )

      if (switchedOffAutomaticChargeIndex !== -1) {
        const switchedOffCharge = charges[switchedOffAutomaticChargeIndex]

        if (!automaticallyDisabledChargeKeys.current.has(getChargeKey(switchedOffCharge))) {
          return
        }

        automaticallyDisabledChargeKeys.current.delete(getChargeKey(switchedOffCharge))
        charges[switchedOffAutomaticChargeIndex] = {
          ...switchedOffCharge,
          isDeleted: false,
          quantity: 1,
          rate: outOfHoursRate,
        }
        toast.info(`Automatic charge Out Of Hours was enabled.`)
        return
      }

      const hasBlockingOutOfHoursCharge = charges.some(
        (charge) => representsOutOfHours(charge) && (charge.isAutomatic || !charge.isDeleted),
      )

      if (hasBlockingOutOfHoursCharge) {
        return
      }

      const outOfHoursCharge = BookingChargeDefaultsByType[BookingChargeType.OutOfHours]
      if (outOfHoursCharge?.type) {
        charges.push({
          ...outOfHoursCharge,
          rate: outOfHoursRate,
        })
        toast.info(`Automatic charge Out Of Hours was added.`)
      }
    },
    [outOfHoursRate],
  )

  const handleOutOfHoursChargesSwitchedOff = useCallback((charges: TBookingChargeForm[]) => {
    const hasActiveAutomaticCharge = charges.some(
      (charge) => isActiveOutOfHoursCharge(charge) && charge?.isAutomatic,
    )

    if (!hasActiveAutomaticCharge) {
      return
    }

    charges.forEach((charge, index) => {
      if (isActiveOutOfHoursCharge(charge) && charge?.isAutomatic) {
        automaticallyDisabledChargeKeys.current.add(getChargeKey(charge))
        charges[index] = { ...charge, isDeleted: true, quantity: 1 }
      }
    })

    toast.info(`Automatic charge Out Of Hours was disabled.`)
  }, [])

  const isOutOfHours = useMemo(
    () =>
      collectionAddresses?.some(
        (address) => isOutOfHoursTimestamp(address?.at) || isOutOfHoursTimestamp(address?.by),
      ) ?? false,
    [collectionAddresses],
  )

  const handleOutOfHoursChargesSync = useCallback(
    (charges: TBookingChargeForm[]) => {
      if (!collectionAddresses?.length || collectionAddresses.length === 0) {
        return
      }

      if (isPricingLocked) {
        return
      }

      charges.forEach((charge) => {
        if (representsOutOfHours(charge)) {
          charge.quantity = 1
        }
      })

      if (isOutOfHours) {
        handleOutOfHoursChargesApplied(charges)
      } else {
        handleOutOfHoursChargesSwitchedOff(charges)
      }
    },
    [
      collectionAddresses,
      handleOutOfHoursChargesApplied,
      handleOutOfHoursChargesSwitchedOff,
      isOutOfHours,
      isPricingLocked,
    ],
  )

  return { handleOutOfHoursChargesSync, outOfHoursRate, isOutOfHours }
}
